import { ComponentPropsWithoutRef, forwardRef, useMemo } from "react";
import clsx from "clsx";
import { useSnapshot } from "valtio";
import { useLocalStorage } from "usehooks-ts";
import { userStore } from "src/js/stores/user/store";

export enum AvatarSize {
  XS = "xs",
  SM = "sm",
  MD = "md",
  LG = "lg",
}

export type AvatarProps = Omit<ComponentPropsWithoutRef<"div">, "size"> & {
  content?: string;
  firstName?: string;
  lastName?: string;
  size?: AvatarSize;
  backgroundExtraClass?: string;
};

export const COLOR_CLASS_NAMES = [
  "bg-red-500",
  "bg-amber-400",
  "bg-green-500",
  "bg-platform-blue-500",
  "bg-[#F096D0]", // light pink
  "bg-marketing-teal-700",
  "bg-[#D862E9]", // dark pink
  "bg-amber-600",
  "bg-blue-grey-500",
  "bg-marketing-purple-500",
];

const SIZES = {
  xs: "w-5 h-5 text-avatar-xs",
  sm: "w-7 h-7 text-avatar-sm",
  md: "w-9 h-9 text-avatar-md",
  lg: "w-11 h-11 text-avatar-lg",
};

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ firstName, lastName, size = AvatarSize.XS, content, className, backgroundExtraClass, ...rest }, ref) => {
    const { user } = useSnapshot(userStore);

    const [userColor] = useLocalStorage<string | undefined>("userColor", undefined);

    const initials = size === AvatarSize.XS ? firstName?.[0] ?? "" : (firstName?.[0] ?? "") + (lastName?.[0] ?? "");

    const colourClassName = useMemo(() => {
      if (userColor && firstName === user.firstname && lastName === user.surname) return userColor;
      if (content || !firstName || !lastName) return "bg-neutral-300";

      const numberHash = [...firstName, ...lastName].reduce((a, b) => {
        a = (a << 5) - a + b.charCodeAt(0);
        return a & a;
      }, 0);
      return COLOR_CLASS_NAMES[Math.abs(numberHash % COLOR_CLASS_NAMES.length)];
    }, [userColor, firstName, user.firstname, user.surname, lastName, content]);

    return (
      <div
        ref={ref}
        className={clsx(SIZES[size], "block rounded-full overflow-hidden border-2 border-white", className)}
        data-testid="li-avatar-container"
        {...rest}
      >
        <div
          className={clsx(
            colourClassName,
            "text-neutral-50 uppercase w-full h-full flex justify-center items-center font-medium",
            backgroundExtraClass
          )}
          data-testid="li-avatar"
        >
          {content ?? initials}
        </div>
      </div>
    );
  }
);
Avatar.displayName = "Avatar";
